import {
  Component,
  computed,
  HostListener,
  inject,
  input,
  InputSignal,
  output,
  OutputEmitterRef,
  signal,
  Signal,
  WritableSignal,
} from '@angular/core';
import { ButtonComponent } from '../button/button.component';
import { DecimalsToCentsPipe } from '../../../pipes/decimals-to-cents/decimals-to-cents.pipe';
import {
  ICorporateDesign,
  ICompanionData,
  ITicket,
  NamedTariffProductDefinition,
  ERelationType,
  TariffProductDefinition,
  TariffProductGroup,
  IDirectTicket,
  EEntityType,
  BREAKPOINT_MD,
  CONFIGURATION_SETTING_KEYS,
} from '@web/dtos';
import { FilterTextsPipe } from '../../../pipes/filterTexts/filterTexts.pipe';
import { LanguagePipe } from '../../../pipes/language/language.pipe';
import { CentsToEurosPipe } from '../../../pipes/cents-to-euros/cents-to-euros.pipe';
import { Router } from '@angular/router';
import { NgClass, NgStyle, NgTemplateOutlet } from '@angular/common';
import {
  IProductInCart,
  ShoppingCartService,
} from '../../../services/shopping-cart/shopping-cart.service';
import { CorporateDesignService } from '../../../services/corporate-design/corporate-design.service';
import { ShoppingCartStore } from '../../../services/api/shopping-cart/shopping-cart-store.service';
import { TicketFrameComponent } from '../../data-display/ticket-frame/ticket-frame.component';
import { DirectTicketShoppingCartItemComponent } from '../direct-ticket-shopping-cart-item/direct-ticket-shopping-cart-item.component';
import { WebTextStore } from '../../../services/api/text/web-text-store.service';
import { ShoppingCartHeadline } from '../../data-display/shopping-cart-headline/shopping-cart-headline.component';
import { ShoppingCartEmpty } from '../../data-display/shopping-cart-empty/shopping-cart-empty';
import { ERoute } from '../../../constants/routes.enum';
import { ConfigurationStore } from '../../../services/api/configuration/configuration-store.service';
import { TemplateStringPipe } from '../../../pipes/template-string/TemplateStringPipe.pipe';

export enum EShoppingCartMenuAction {
  info,
  editOptions,
  editOwner,
  delete,
}

export enum EPage {
  Landing,
  Configuration,
}

@Component({
  selector: 'aw-direct-ticket-configuration-shopping-cart',
  imports: [
    ButtonComponent,
    FilterTextsPipe,
    LanguagePipe,
    CentsToEurosPipe,
    NgTemplateOutlet,
    NgClass,
    TicketFrameComponent,
    DirectTicketShoppingCartItemComponent,
    NgStyle,
    ShoppingCartHeadline,
    ShoppingCartEmpty,
    TemplateStringPipe,
  ],
  templateUrl: './direct-ticket-configuration-shopping-cart.component.html',
})
export class DirectTicketConfigurationShoppingCartComponent {
  public readonly page: InputSignal<EPage> = input.required();
  public readonly isDisabled: InputSignal<boolean> = input(false);

  protected readonly shoppingCartService: ShoppingCartService =
    inject(ShoppingCartService);
  protected readonly shoppingCartStore: ShoppingCartStore =
    inject(ShoppingCartStore);
  protected readonly corporateDesignService: CorporateDesignService = inject(
    CorporateDesignService
  );
  protected readonly configurationStore: ConfigurationStore =
    inject(ConfigurationStore);
  protected readonly textStore: WebTextStore = inject(WebTextStore);
  private readonly router: Router = inject(Router);

  private readonly decimalsToCentsPipe: DecimalsToCentsPipe =
    new DecimalsToCentsPipe();
  protected readonly ERelationType: typeof ERelationType = ERelationType;
  protected readonly EEntityType: typeof EEntityType = EEntityType;

  public readonly onButtonClick: OutputEmitterRef<void> = output();
  public readonly onSubmenuClick: OutputEmitterRef<{
    ticket: ITicket | undefined;
    productDefinition: NamedTariffProductDefinition;
  }> = output();

  public readonly productDefinition: Signal<
    TariffProductDefinition | undefined
  > = this.shoppingCartService.currentProductDefinition;
  public readonly productGroup: Signal<TariffProductGroup> = this
    .shoppingCartService
    .directTicketConfiguration as Signal<TariffProductGroup>;
  protected readonly windowWidth: WritableSignal<number> = signal(
    window.innerWidth
  );
  protected readonly mobileProductIndex: WritableSignal<number> = signal(0);
  protected readonly isMobile: Signal<boolean> = computed(() => {
    const mdBreakpoint: number = BREAKPOINT_MD;
    return this.windowWidth() < mdBreakpoint;
  });
  protected readonly isTicketOwnerDataRequired: Signal<boolean> =
    this.shoppingCartService.isTicketOwnerDataRequired;

  protected readonly corporateDesign: Signal<ICorporateDesign> =
    this.corporateDesignService.corporateDesign;
  protected readonly isOnLandingPage: Signal<boolean> = computed(() => {
    switch (this.page()) {
      case EPage.Landing:
        return true;
      default:
        return false;
    }
  });
  protected readonly ticket: Signal<ITicket | undefined> = computed(() => {
    return this.productGroup().name &&
      this.shoppingCartService.tickets().length > 0
      ? this.shoppingCartService
          .tickets()
          .find(
            (ticket: IDirectTicket) => ticket.name === this.productGroup().name
          )?.ticketData
      : undefined;
  });

  protected readonly totalTicketAmount: Signal<number> = computed(() => {
    return this.shoppingCartService
      .ticketsInCart()
      .reduce(
        (acc: number, curr: IProductInCart) =>
          acc + (curr.productForm?.value?.amount ?? 0),
        0
      );
  });

  protected readonly totalNetPrice: Signal<number> = computed(() => {
    return this.shoppingCartService.ticketsInCart().reduce((acc, curr) => {
      return (
        acc +
        this.decimalsToCentsPipe.transform(
          curr.productForm!.value!.product!.price.netPrice
        ) *
          (curr.productForm?.value?.amount ?? 0)
      );
    }, 0);
  });

  protected readonly totalVatPrice: Signal<number> = computed(() => {
    return this.shoppingCartService.ticketsInCart().reduce((acc, curr) => {
      return (
        acc +
        this.decimalsToCentsPipe.transform(
          curr.productForm!.value!.product!.price.taxPrice
        ) *
          (curr.productForm?.value?.amount ?? 0)
      );
    }, 0);
  });

  protected readonly totalTicketPrice: Signal<number> = computed(() => {
    return this.shoppingCartService.ticketsInCart().reduce((acc, curr) => {
      return (
        acc +
        this.decimalsToCentsPipe.transform(
          curr.productForm!.value!.product!.price.priceIncludingVat
        ) *
          (curr.productForm?.value?.amount ?? 0)
      );
    }, 0);
  });

  protected readonly currentTickets: Signal<IProductInCart[]> = computed(() => {
    return this.shoppingCartService.ticketsInCart();
  });

  protected getCompanions(product: IProductInCart): ICompanionData[] {
    return (
      product.owner?.getRawValue().flatMap(owner => owner.companions ?? []) ??
      []
    );
  }

  @HostListener('window:resize', ['$event'])
  onResize() {
    this.windowWidth.set(window.innerWidth);
  }

  protected addAdditionalTicket(): void {
    this.router.navigate([`/${ERoute.directTickets}`]);
  }

  emptyShoppingCart(): void {
    this.shoppingCartService.emptyShoppingCart();
  }

  protected nextProduct(): void {
    const currentIndex: number = this.mobileProductIndex();
    this.mobileProductIndex.set(currentIndex + 1);
  }

  protected previousProduct(): void {
    const currentIndex: number = this.mobileProductIndex();
    this.mobileProductIndex.set(currentIndex - 1);
  }

  protected proceedToPersonalData(): void {
    this.shoppingCartService.continueEditingExistingTicket.set(true);
    this.router.navigate([
      `/${ERoute.directTicketConfiguration}/${ERoute.personalData}/`,
    ]);
  }

  protected readonly CONFIGURATION_SETTING_KEYS: typeof CONFIGURATION_SETTING_KEYS =
    CONFIGURATION_SETTING_KEYS;
}
